import { useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import Datepicker from "react-tailwindcss-datepicker";

function parseYmdToDate(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const d = dayjs(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d.toDate() : null;
}

function toYmd(date) {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

export default function FormDatePicker({
  id,
  label,
  name,
  value = "",
  onChange,
  disabled,
  error,
  className = "",
  min,
  max,
  placeholder = "Select date",
  showShortcuts = true,
}) {
  const inputId = id || name;

  const pickerValue = useMemo(() => {
    const d = parseYmdToDate(value);
    return {
      startDate: d,
      endDate: d,
    };
  }, [value]);

  const minDate = useMemo(() => (min ? parseYmdToDate(min) : undefined), [min]);
  const maxDate = useMemo(() => (max ? parseYmdToDate(max) : undefined), [max]);

  const handleChange = (next) => {
    const start = next?.startDate ?? null;
    const ymd = toYmd(start);
    onChange({
      target: { value: ymd, name },
      currentTarget: { value: ymd, name },
      type: "change",
    });
  };

  const inputClassName = [
    "!w-full !rounded-lg !border !px-3 !py-2.5 !text-sm !font-normal !outline-none !transition",
    "!cursor-pointer !select-none",
    "!shadow-none focus:!ring-2 focus:!ring-brand-600",
    error
      ? "!border-red-400 !bg-red-50/40"
      : "!border-slate-500 !bg-white",
  ].join(" ");

  const shortcutConfig = useMemo(() => {
    if (!showShortcuts) return undefined;
    return {
      shortcuts: {
        today: "Today",
        yesterday: "Yesterday",
        last3Days: {
          text: "Last 3 days",
          period: {
            start: new Date(new Date().setDate(new Date().getDate() - 3)),
            end: new Date(new Date().setDate(new Date().getDate() - 1)),
          },
        },
        next8Days: {
          text: "Next 8 days",
          period: {
            start: new Date(new Date().setDate(new Date().getDate() + 1)),
            end: new Date(new Date().setDate(new Date().getDate() + 8)),
          },
        },
        currentMonth: "This month",
        pastMonth: "Last month",
      },
      footer: {
        cancel: "Cancel",
        apply: "Apply",
      },
    };
  }, [showShortcuts]);

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        className="mb-1 block text-xs font-semibold text-black"
      >
        {label}
      </label>
      <div className="[&_.placeholder-gray-400]:text-text-500">
        <Datepicker
          inputId={inputId}
          inputName={name}
          asSingle
          useRange={false}
          primaryColor="blue"
          popoverDirection="down"
          placeholder={placeholder}
          value={pickerValue}
          onChange={handleChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          displayFormat="MM/DD/YYYY"
          readOnly
          showShortcuts={showShortcuts}
          showFooter={showShortcuts}
          configs={shortcutConfig}
          containerClassName="relative w-full [&_.relative]:w-full"
          inputClassName={inputClassName}
        />
      </div>
      {error ? (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-xs font-medium text-red-600"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

FormDatePicker.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  placeholder: PropTypes.string,
  showShortcuts: PropTypes.bool,
};
